/**
 * Anthropometric calculation utilities
 * Handles calculations for WHR (Waist-to-Hip Ratio) and IBW (Ideal Body Weight)
 */

/**
 * Calculate Waist-to-Hip Ratio (WHR)
 * Formula: WHR = Waist circumference ÷ Hip circumference
 * Works the same in both metric and imperial systems
 */
export const calculateWHR = (waist: number, hip: number): number | null => {
  const w = Number(waist);
  const h = Number(hip);
  if (!w || !h || w <= 0 || h <= 0 || isNaN(w) || isNaN(h)) {
    return null;
  }
  return Number((w / h).toFixed(2));
};

/**
 * Calculate Ideal Body Weight (IBW) in metric system
 * Uses Devine Formula (metric-converted)
 * 
 * Men: IBW (kg) = 50 + 2.3 × (height in inches − 60)
 * Women: IBW (kg) = 45.5 + 2.3 × (height in inches − 60)
 * 
 * Note: Height is converted from cm to inches, then the standard Devine formula is applied
 * Example: Male, 168 cm = 66.14 in, IBW = 50 + (2.3 × 6.14) = 64.12 kg
 */
export const calculateIBWMetric = (heightCm: number, isMale: boolean): number | null => {
  if (!heightCm || heightCm <= 0) {
    return null;
  }
  
  // Convert cm to inches
  const heightInches = heightCm / 2.54;
  
  if (isMale) {
    // Men: IBW (kg) = 50 + 2.3 × (height in inches − 60)
    const ibwKg = 50 + (2.3 * (heightInches - 60));
    return Number(ibwKg.toFixed(1));
  } else {
    // Women: IBW (kg) = 45.5 + 2.3 × (height in inches − 60)
    const ibwKg = 45.5 + (2.3 * (heightInches - 60));
    return Number(ibwKg.toFixed(1));
  }
};

/**
 * Calculate Ideal Body Weight (IBW) in imperial system
 * Uses Devine Formula (original)
 * 
 * Men: IBW (lbs) = 110 lbs + (5 lbs × each inch over 5 feet)
 * Women: IBW (lbs) = 100 lbs + (5 lbs × each inch over 5 feet)
 * 
 * If height is below 5 ft: Subtract 5 lbs for each inch below 5 ft
 */
export const calculateIBWImperial = (heightInches: number, isMale: boolean): number | null => {
  if (!heightInches || heightInches <= 0) {
    return null;
  }
  
  const fiveFeetInches = 60; // 5 feet = 60 inches
  
  if (isMale) {
    // Men: IBW (lbs) = 110 + (5 × (inches over 5 feet))
    if (heightInches >= fiveFeetInches) {
      const ibwLbs = 110 + (5 * (heightInches - fiveFeetInches));
      return Number(ibwLbs.toFixed(1));
    } else {
      // Below 5 feet: subtract 5 lbs for each inch below
      const ibwLbs = 110 - (5 * (fiveFeetInches - heightInches));
      return Number(ibwLbs.toFixed(1));
    }
  } else {
    // Women: IBW (lbs) = 100 + (5 × (inches over 5 feet))
    if (heightInches >= fiveFeetInches) {
      const ibwLbs = 100 + (5 * (heightInches - fiveFeetInches));
      return Number(ibwLbs.toFixed(1));
    } else {
      // Below 5 feet: subtract 5 lbs for each inch below
      const ibwLbs = 100 - (5 * (fiveFeetInches - heightInches));
      return Number(ibwLbs.toFixed(1));
    }
  }
};

/**
 * Convert inches to cm
 */
export const inchesToCm = (inches: number): number => {
  return inches * 2.54;
};

/**
 * Convert cm to inches
 */
export const cmToInches = (cm: number): number => {
  const n = Number(cm);
  return isNaN(n) ? 0 : n / 2.54;
};

/**
 * Convert inches to cm with proper rounding
 * Always rounds to 1 decimal place for consistency
 * 
 * @param inches - The value in inches
 * @returns The value in cm rounded to 1 decimal place
 * 
 * @example
 * convertInchesToCm(44) // Returns 111.8
 * convertInchesToCm(45) // Returns 114.3
 * convertInchesToCm(100) // Returns 254.0
 */
export const convertInchesToCm = (inches: number): number => {
  const cm = inchesToCm(inches);
  // Always round to 1 decimal place for precision
  return Math.round(cm * 10) / 10;
};

/**
 * Convert cm to inches with proper formatting for display
 * If the original value was a whole number, returns whole number
 * Otherwise returns 1 decimal place
 * 
 * @param cm - The value in cm
 * @param originalInches - The original inches value (optional, for determining if it was whole number)
 * @returns The value in inches, formatted appropriately
 */
export const convertCmToInches = (cm: number, originalInches?: number): number => {
  const inches = cmToInches(cm);
  // If original was a whole number, round to whole number, otherwise 1 decimal
  if (originalInches !== undefined && originalInches % 1 === 0) {
    return Math.round(inches);
  }
  return Math.round(inches * 10) / 10;
};

